import React, { useCallback, useEffect, useState } from "react";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import Table, { Column, DataSourceItem } from "../../Components/Atoms/Table";
import Tag from "../../Components/Atoms/Tag";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import TablePaginationFooter from "../../Components/Atoms/Table/TablePaginationFooter";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import { useTransactionStore } from "../../Store/Transaction";
import { PAGINATION_KEYS, usePagination } from "../../Store/Pagination";
import { createSortFunction } from "../../Utils/TableUtils";
import LoadingPage from "../LoadingPage";
import { PaymentRoutes } from "../../Contracts/Routes";
import { toLocalDateString } from "../../Utils/DateUtils";
import {
  AdyenTransactionableType,
  AdyenTransactionStatus,
  getPaymentMethodDetails,
  Transaction
} from "../../Contracts/Transaction";
import { useDebounce } from "../../Utils/ActionUtils";
import { ReactComponent as CloseIcon } from "../../Assets/ActionIcons/CloseIcon.svg";
import { useTeamStore } from "../../Store/Team";
import TransactionListActions from "./TransactionListActions";
import { AdyenCustomerType } from "../../Contracts/Customer";

const TransactionListPage = () => {
  const [sortParamValue, setSortParamValue] = useState<string>("created_at:desc");

  const { limit, page, setLimit, setPage } = usePagination(PAGINATION_KEYS.TRANSACTION_LIST, {
    limit: 25,
    page: 1
  });

  const [actionsOpen, setActionsOpen] = useState<number>(0);
  const { formatCurrency, getCurrentTeam, currentTeam } = useTeamStore();

  const conditionalActionsColumn: Column<Transaction>[] = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <TransactionListActions
          transaction={d.data}
          open={d.data.id === actionsOpen}
          onClose={() => setActionsOpen(0)}
          onOpen={() => setActionsOpen(d.data.id)}
        />
      )
    }
  ];

  const columns: Column<Transaction>[] = [
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: d =>
        d.isUnmappedCustomer ? (
          <div>{d.client}</div>
        ) : (
          <a
            href={`${window.location.origin}${PaymentRoutes.CUSTOMERS}/${d.data.customer_id}`}
            className="text-blue-600"
          >
            {d.client}
          </a>
        )
    },
    {
      title: "Invoice / Document",
      dataIndex: "entity",
      key: "entity",
      render: d => (
        <span>
          {d.data.transactionable_type === AdyenTransactionableType.CollectibleInvoice
            ? `Invoice ${d.data.transaction_identifier}`
            : d.data.transaction_identifier}
        </span>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sortClicked: createSortFunction("status", setSortParamValue),
      render: d => (
        <Tag
          color={
            d.data.status === AdyenTransactionStatus.Completed
              ? "lime"
              : d.data.status === AdyenTransactionStatus.Processing
                ? "yellow"
                : d.data.status === AdyenTransactionStatus.Refunded
                  ? "gray"
                  : d.data.status === AdyenTransactionStatus.Failed ||
                      d.data.status === AdyenTransactionStatus.Chargeback
                    ? "red"
                    : d.data.status === AdyenTransactionStatus.RefundInitiated
                      ? "blue"
                      : "gray"
          }
        >
          {d.data.status}
        </Tag>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sortClicked: createSortFunction("total_amount", setSortParamValue),
      render: d => (
        <span className="underline decoration-dashed decoration-gray-400 underline-offset-4 cursor-pointer group">
          <div className="relative">
            <div className="group-hover:block hidden absolute w-80 top-6 left-0 bg-white shadow-lg p-2 rounded z-40">
              <div className="mb-3">Amount Breakup</div>
              <div className="grid grid-cols-5 border-b">
                <div className="col-span-3 border-y py-2 font-semibold">Type</div>
                <div className="col-span-2 border-y py-2 font-semibold text-right">Amount</div>
                <div className="col-span-3 border-left flex flex-col py-2 border-r">
                  <div>Invoice Amount</div>
                </div>
                <div className="col-span-2 py-2 text-right">
                  <div>{formatCurrency(d.data.total_amount - d.data.transaction_fee)}</div>
                </div>
                {d.data.transaction_fee !== 0 && (
                  <>
                    <div className="col-span-3 border-left py-2 border-r">Transaction Fee</div>
                    <div className="col-span-2 py-2 text-right">
                      <div>+{d.transaction_fee}</div>
                    </div>
                  </>
                )}
                <div className="col-span-3 border-left py-2 border-r">Zomentum Fee</div>
                <div className="col-span-2 py-2 text-right">
                  <div>-{d.commission_amount}</div>
                </div>
              </div>
              <div className="flex items-center justify-end py-3 space-x-2">
                <div className="font-semibold">Net Amount:</div>
                <div>{d.net_amount}</div>
              </div>
            </div>
          </div>
          {d.total_amount}
        </span>
      )
    },
    {
      title: "Net Amount",
      dataIndex: "net_amount",
      key: "net_amount",
      sortClicked: createSortFunction("net_amount", setSortParamValue)
    },
    {
      title: "Date Created",
      dataIndex: "created_at",
      key: "created_at",
      sortClicked: createSortFunction("created_at", setSortParamValue),
      render: d => (
        <span title={toLocalDateString(d.data.created_at, true)}>
          {toLocalDateString(d.data.created_at)}
        </span>
      ),
      defaultSortOrder: "desc"
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: d => {
        const details = getPaymentMethodDetails(d.data.payment_method);
        return (
          <div className="flex items-center space-x-1">
            <img src={details.logo} alt={details.name} className="h-8" />
            <div>{d.data.payment_method_summary}</div>
          </div>
        );
      }
    },
    ...(currentTeam?.early_access_features?.["manual-refunds"] ? conditionalActionsColumn : [])
  ];

  const [search, setSearch] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 1500);

  const { isLoading, transactions, transactionsCount, getTransactions } = useTransactionStore();
  const { getAccessTokenSilently } = useAuth0();

  const refreshThings = useCallback(async () => {
    await getTransactions(
      getAccessTokenSilently,
      limit,
      sortParamValue,
      page,
      undefined,
      debouncedSearch
    );
  }, [getTransactions, getAccessTokenSilently, limit, sortParamValue, page, debouncedSearch]);
  useEffect(() => {
    getCurrentTeam(getAccessTokenSilently);
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page, getCurrentTeam, getAccessTokenSilently]);

  const dataSource: DataSourceItem<Transaction>[] = transactions.map(transaction => ({
    key: transaction.id.toString(),
    client: transaction.customer_name,
    entity: transaction.customer_type,
    total_amount: formatCurrency(transaction.total_amount),
    net_amount: formatCurrency(transaction.net_amount),
    transaction_fee: formatCurrency(transaction.transaction_fee),
    commission_amount: formatCurrency(transaction.commission_amount),
    payment_method: transaction.payment_method,
    data: transaction,
    isUnmappedCustomer: transaction.customer_type === AdyenCustomerType.PaymentsGrowCustomer
  }));

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };

  return (
    <PaymentsAppLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        <div className="flex items-center space-x-2">
          <input
            placeholder="Type to search"
            value={search || ""}
            onChange={handleSearchInputChange}
            className={`border border-gray-300 rounded p-1 my-1 w-48`}
          />
          {search && (
            <CloseIcon
              className="h-3 w-3 hover:scale-110 transition-all duration-500 hover:text-red-600 cursor-pointer"
              onClick={() => setSearch(undefined)}
            />
          )}
        </div>
        <div className="flex flex-grow flex-col overflow-y-auto">
          {isLoading && transactions.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
          {!isLoading && transactions.length === 0 && (
            <div className="text-center text-gray-400 mt-12">No transactions found</div>
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          page={page}
          setPage={setPage}
          totalRecords={transactionsCount}
          isLoading={isLoading}
        />
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(TransactionListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
